import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import { Notice } from '../labour/shared';
import insightsService from '../../services/insights';
import { errorMessage } from '../../utils/apiResponse';
import type { HarvestProgress, ProgressRow } from '../../types/insights';
import { num, pct } from './insightsUtils';

const th = 'text-right px-3 py-2 font-medium whitespace-nowrap';
const td = 'px-3 py-2 text-right whitespace-nowrap';

function Bar({ value }: { value: number | null }) {
  if (value == null) return <span className="text-neutral-400">—</span>;
  const w = Math.min(100, Math.max(0, value));
  return (
    <span className="flex items-center gap-2 justify-end">
      <span className="w-16 h-2 rounded-full bg-neutral-200 overflow-hidden" aria-hidden>
        <span className={`block h-full ${value >= 100 ? 'bg-green-600' : 'bg-indigo-500'}`} style={{ width: `${w}%` }} />
      </span>
      <span>{pct(value)}</span>
    </span>
  );
}

function windowText(r: ProgressRow): string {
  if (!r.window_end) return r.window_start ? `from ${r.window_start}` : '—';
  if (r.days_left_in_window == null) return r.window_end;
  if (r.days_left_in_window < 0) return `ended ${-r.days_left_in_window} d ago`;
  return `${r.days_left_in_window} d left`;
}

/**
 * Crop estimate per cultivar area against bins picked: progress, today's bins,
 * the last week's pick rate, days to finish, and whether that fits the harvest
 * window. Creators and admins type estimates straight into the table.
 */
export default function HarvestProgressPanel({ season, farmId, canEdit }: { season: number; farmId: number | null; canEdit: boolean }) {
  const [data, setData] = useState<HarvestProgress | null>(null);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await insightsService.harvestProgress({ season, farm_id: farmId ?? undefined }));
      setDraft({});
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load harvest progress'));
    }
  }, [season, farmId]);
  useEffect(() => { load(); }, [load]);

  const dirty = Object.keys(draft).length > 0;
  const byFarm = useMemo(() => {
    const m = new Map<string, ProgressRow[]>();
    for (const r of data?.rows ?? []) {
      if (!m.has(r.farm_name)) m.set(r.farm_name, []);
      m.get(r.farm_name)!.push(r);
    }
    return [...m.entries()];
  }, [data]);

  if (!data) return <div className="text-sm text-neutral-500 py-6 text-center">{error ?? 'Loading…'}</div>;

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const items = Object.entries(draft).map(([id, v]) => {
        const row = data.rows.find(r => r.cultivar_area_id === Number(id));
        return { cultivar_area_id: Number(id), est_bins: v.trim() === '' ? null : Number(v), est_tonnes: row?.est_tonnes ?? null, notes: row?.estimate_notes ?? null };
      });
      setData(await insightsService.saveEstimates(season, items));
      setDraft({});
      setSaved(true);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the estimates'));
    } finally {
      setSaving(false);
    }
  };

  // The yield-history suggestion where there is one, last season's bins otherwise.
  const fillSuggested = () => {
    const next = { ...draft };
    for (const r of data.rows) {
      const v = r.suggested_bins ?? r.last_season_bins;
      if (r.est_bins == null && next[r.cultivar_area_id] == null && v) next[r.cultivar_area_id] = String(v);
    }
    setDraft(next);
  };

  const t = data.totals;
  const behind = data.rows.filter(r => r.behind_window);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-neutral-200 rounded-lg p-3"><p className="text-xs text-neutral-500">Picked</p><p className="text-lg font-semibold">{num(t.bins)} bins</p><p className="text-xs text-neutral-500">{num(t.tonnes, 2)} t</p></div>
        <div className="border border-neutral-200 rounded-lg p-3"><p className="text-xs text-neutral-500">Today</p><p className="text-lg font-semibold">{num(t.bins_today)} bins</p></div>
        <div className="border border-neutral-200 rounded-lg p-3"><p className="text-xs text-neutral-500">Estimated crop</p><p className="text-lg font-semibold">{t.est_bins ? `${num(t.est_bins)} bins` : '—'}</p></div>
        <div className="border border-neutral-200 rounded-lg p-3"><p className="text-xs text-neutral-500">Progress</p><p className="text-lg font-semibold">{pct(t.progress_pct)}</p><p className="text-xs text-neutral-500">of estimated areas</p></div>
      </div>
      {behind.length > 0 && (
        <Notice tone="warn">
          <span className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>At the last week’s pick rate {behind.length === 1 ? 'one area' : `${behind.length} areas`} will not finish inside the harvest window: {behind.map(r => r.cultivar_area_name).join(', ')}.</span>
          </span>
        </Notice>
      )}
      {data.unplaced_bins > 0 && <Notice>{num(data.unplaced_bins)} bins this season have no cultivar area yet — confirm their block in the Shed.</Notice>}
      {error && <Notice tone="error">{error}</Notice>}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={fillSuggested}>Use suggested estimates where blank</Button>
          <Button size="sm" variant="primary" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save estimates'}</Button>
          {saved && !dirty && <span className="text-sm text-green-700">Saved</span>}
        </div>
      )}
      {data.rows.length === 0 ? <p className="text-sm text-neutral-500 py-6 text-center">No cultivar areas yet.</p> : (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Area</th>
                <th className={th}>Estimate (bins)</th>
                <th className={th}>Picked</th>
                <th className={th}>Today</th>
                <th className={th}>Progress</th>
                <th className={th}>Bins/day</th>
                <th className={th}>Days to go</th>
                <th className={th}>Window</th>
                <th className={th}>Last season</th>
                <th className={th}>Suggested</th>
              </tr>
            </thead>
            {byFarm.map(([farm, rows]) => (
              <tbody key={farm} className="divide-y divide-neutral-100">
                <tr className="bg-neutral-50/60"><td colSpan={10} className="px-3 py-1.5 text-xs font-semibold text-neutral-600">{farm}</td></tr>
                {rows.map(r => (
                  <tr key={r.cultivar_area_id} className={r.behind_window ? 'bg-red-50/40' : undefined}>
                    <td className="px-3 py-2 min-w-[11rem]">
                      <p className="text-neutral-900">{r.cultivar_area_name}</p>
                      <p className="text-xs text-neutral-500">{[r.block_name, r.cultivar_name, r.area_hectares ? `${num(r.area_hectares)} ha` : null].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className={td}>
                      {canEdit ? (
                        <input type="number" inputMode="decimal" min={0} aria-label={`Estimated bins for ${r.cultivar_area_name}`}
                          className="w-20 border border-neutral-300 rounded-md px-2 py-1 text-right text-sm"
                          value={draft[r.cultivar_area_id] ?? (r.est_bins ?? '')}
                          onChange={e => setDraft(d => ({ ...d, [r.cultivar_area_id]: e.target.value }))} />
                      ) : num(r.est_bins)}
                    </td>
                    <td className={td}>{num(r.bins)}{r.remaining_bins != null && r.remaining_bins > 0 && <span className="block text-xs text-neutral-500">{num(r.remaining_bins)} to go</span>}</td>
                    <td className={td}>{r.bins_today ? num(r.bins_today) : '—'}</td>
                    <td className={td}><Bar value={r.progress_pct} /></td>
                    <td className={td}>{num(r.bins_per_pick_day)}</td>
                    <td className={`${td} ${r.behind_window ? 'text-red-700 font-medium' : ''}`}>{r.days_to_finish ?? '—'}</td>
                    <td className={td}>{windowText(r)}</td>
                    <td className={td}>{num(r.last_season_bins)}</td>
                    <td className={td} title={r.suggestion_basis === 'young_trend' ? 'Young trees: last season plus their recent rise'
                      : r.suggestion_basis === 'average' ? 'Average of the last three seasons per hectare' : r.suggestion_basis ? 'Last season per hectare' : undefined}>
                      {num(r.suggested_bins, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
      <p className="text-xs text-neutral-500">Days to go uses the bins per picking day over the last 7 days. Windows come from the cultivar area’s harvest window for this season.</p>
    </div>
  );
}
